import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  CardGiftcardOutlined as GiftCardIcon,
  VolunteerActivismOutlined as DonationIcon,
  ShoppingCartOutlined as SaleIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import {
  CommonInput,
  CommonSelect,
  CommonViewToggle,
} from "../../../../Components/ui";

const SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "valueHigh", label: "Highest Value" },
  { value: "priceLow", label: "Lowest Price" },
  { value: "expiringSoon", label: "Expiring Soon" },
];

const CATEGORY_TABS = [
  { id: "all", label: "All Cards", icon: GiftCardIcon, color: colors.primary.main },
  { id: "donation", label: "Free Vouchers", icon: DonationIcon, color: colors.amber[600] },
  { id: "sale", label: "Direct Sale", icon: SaleIcon, color: colors.emerald[600] },
];

const GiftCardsFilters = ({
  activeTab = "all",
  onTabChange,
  searchQuery = "",
  onSearchChange,
  sortBy = "newest",
  onSortChange,
  viewMode = "grid",
  onViewModeChange,
  countsByTab = { all: 0, donation: 0, sale: 0 },
  onRefresh,
  refreshing = false,
}) => {
  return (
    <Card
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        mb: 3.5,
      }}
    >
      <Stack spacing={2.5}>
        {/* Top: Chip-Style Category Tabs */}
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          sx={{
            overflowX: "auto",
            pb: { xs: 1, sm: 0 },
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
          }}
        >
          {CATEGORY_TABS.map((tab) => {
            const isSelected = String(activeTab) === String(tab.id);
            const TabIcon = tab.icon;
            const count = countsByTab[tab.id] !== undefined ? countsByTab[tab.id] : 0;

            return (
              <Box
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 1,
                  borderRadius: borderRadiusPx.lg,
                  cursor: "pointer",
                  userSelect: "none",
                  whiteSpace: "nowrap",
                  transition: "all 0.2s ease-in-out",
                  backgroundColor: isSelected ? colors.primary.main : colors.slate[50],
                  border: `1px solid ${isSelected ? colors.primary.main : colors.slate[200]}`,
                  boxShadow: isSelected ? "0 4px 12px rgba(37, 99, 235, 0.25)" : "none",
                  "&:hover": {
                    backgroundColor: isSelected ? colors.primary.dark : colors.slate[100],
                    borderColor: isSelected ? colors.primary.dark : colors.slate[300],
                  },
                }}
              >
                <TabIcon sx={{ fontSize: 16, color: isSelected ? colors.neutral.white : tab.color }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isSelected ? 700 : 600,
                    fontSize: "0.875rem",
                    color: isSelected ? colors.neutral.white : colors.slate[700],
                  }}
                >
                  {tab.label}
                </Typography>
                <Box
                  sx={{
                    px: 0.85,
                    py: 0.15,
                    borderRadius: borderRadiusPx.pill,
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    backgroundColor: isSelected ? "rgba(255, 255, 255, 0.25)" : colors.slate[200],
                    color: isSelected ? colors.neutral.white : colors.slate[700],
                  }}
                >
                  {count}
                </Box>
              </Box>
            );
          })}
        </Stack>

        {/* Bottom: Search, Sort, View Toggle & Refresh Controls */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          {/* Search Input */}
          <Box sx={{ width: { xs: "100%", md: "380px" } }}>
            <CommonInput
              fullWidth
              size="small"
              placeholder="Search by Card #ID, token, creator address..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.slate[400], fontSize: 20 }} />
                </InputAdornment>
              }
              endAdornment={
                searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => onSearchChange("")} sx={{ p: 0.25 }}>
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }
            />
          </Box>

          {/* Sort, View Toggle & Refresh */}
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{
              justifyContent: { xs: "space-between", md: "flex-end" },
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {/* Sort Dropdown */}
            <Box sx={{ minWidth: 170 }}>
              <CommonSelect
                fullWidth
                size="small"
                value={sortBy}
                onChange={(e) => onSortChange(e.target.value)}
                options={SORT_OPTIONS}
              />
            </Box>

            {/* View Mode Toggle */}
            {onViewModeChange && (
              <CommonViewToggle
                value={viewMode}
                onChange={onViewModeChange}
                size="small"
              />
            )}

            {/* Refresh Button */}
            {onRefresh && (
              <Tooltip title="Refresh Gift Cards">
                <span>
                  <IconButton
                    size="small"
                    onClick={onRefresh}
                    disabled={refreshing}
                    sx={{
                      bgcolor: colors.slate[50],
                      border: `1px solid ${colors.slate[200]}`,
                      color: colors.slate[600],
                      p: 0.85,
                      "&:hover": {
                        bgcolor: colors.blue[50],
                        color: colors.primary.main,
                        borderColor: colors.blue[200],
                      },
                    }}
                  >
                    {refreshing ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <RefreshIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
};

GiftCardsFilters.propTypes = {
  activeTab: PropTypes.string,
  onTabChange: PropTypes.func.isRequired,
  searchQuery: PropTypes.string,
  onSearchChange: PropTypes.func.isRequired,
  sortBy: PropTypes.string,
  onSortChange: PropTypes.func.isRequired,
  viewMode: PropTypes.oneOf(["grid", "table"]),
  onViewModeChange: PropTypes.func,
  countsByTab: PropTypes.shape({
    all: PropTypes.number,
    donation: PropTypes.number,
    sale: PropTypes.number,
  }),
  onRefresh: PropTypes.func,
  refreshing: PropTypes.bool,
};

export default GiftCardsFilters;
